// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setFieldValue from "@insite/client-framework/Store/Components/ContactUsForm/Handlers/SetFieldValue";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ContactFormContext } from "@insite/content-library/Widgets/ContactUs/ContactForm";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    label = "label",
    hintText = "hintText",
    isRequired = "isRequired",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.label]: string;
        [fields.hintText]: string;
        [fields.isRequired]: boolean;
    };
}

const mapPostalCodeToProps = (postalCode: ApplicationState) => ({
    PostalCode: postalCode.components.contactUsForm.fieldValues["PostalCode"],
});

const mapDispatchToProps = {
    setFieldValue,
};

type Props = OwnProps & ReturnType<typeof mapPostalCodeToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface postalCodeStyles {
    postalCodeTextField?: TextFieldPresentationProps;
}

export const postalCodeStyles: postalCodeStyles = {};

const styles = postalCodeStyles;

const postalCode: React.FC<Props> = ({ fields, PostalCode, setFieldValue }) => {
    const { label, hintText, isRequired } = fields;
    const contactFormContext = React.useContext(ContactFormContext);
    const [postalCodeErrorMessage, setPostalCodeErrorMessage] = React.useState("");

    React.useEffect(() => {
        contactFormContext.validators.PostalCode = validatePostalCode;
        if (typeof PostalCode !== "undefined") {
            validatePostalCode();
        }
    }, [PostalCode]);

    React.useEffect(() => {
        contactFormContext.validators.PostalCode = validatePostalCode;
    }, [isRequired]);

    const validatePostalCode = () => {
        const errorMessage = isRequired && !PostalCode ? (siteMessage("Field_Required", label) as string) : "";
        setPostalCodeErrorMessage(errorMessage);
        return !errorMessage;
    };

    const postalCodeChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setFieldValue({ key: "PostalCode", value: event.target.value });
    };

    return (
        <TextField
            {...styles.postalCodeTextField}
            label={label}
            required={isRequired}
            placeholder={hintText}
            value={PostalCode || ""}
            error={postalCodeErrorMessage}
            onChange={postalCodeChangeHandler}
        />
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapPostalCodeToProps, mapDispatchToProps)(postalCode),
    definition: {
        group: "Contact Us",
        icon: "input-pipe",
        fieldDefinitions: [
            {
                name: fields.label,
                displayName: "Label",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Postal Code",
                isRequired: true,
            },
            {
                name: fields.hintText,
                displayName: "Hint Text",
                editorTemplate: "TextField",
                fieldType: "Translatable",
                defaultValue: "Postal Code",
                isRequired: true,
            },
            {
                name: fields.isRequired,
                displayName: "Required Field",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
